import { useEffect, useRef, useState } from 'react';

// Hook for scroll animations
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px 0px -50px 0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
};

// Component wrapper for scroll animations
export const ScrollReveal = ({ 
  children, 
  animation = 'fade-up', 
  delay = 0,
  duration = 600,
  className = ''
}) => {
  const [ref, isVisible] = useScrollAnimation();

  const animationStyles = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0) translateX(0) scale(1)' : getInitialTransform(animation),
    transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`
  };

  return (
    <div ref={ref} style={animationStyles} className={className}>
      {children}
    </div>
  );
};

const getInitialTransform = (animation) => {
  switch (animation) {
    case 'fade-up':
      return 'translateY(40px)';
    case 'fade-down':
      return 'translateY(-40px)';
    case 'fade-left':
      return 'translateX(40px)';
    case 'fade-right':
      return 'translateX(-40px)';
    case 'zoom-in':
      return 'scale(0.9)';
    case 'zoom-out':
      return 'scale(1.1)';
    default:
      return 'translateY(40px)';
  }
};

export default ScrollReveal;
